import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Input, OnInit, Renderer2, ViewChild } from '@angular/core';
import { QueryObjectsModel, QueryOutputMode } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { HighlightSyntax, _AbcTreeNode } from '@barthauer/abc/ui-components';
import { Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { AbcQueriesEditorService } from '../../../abc-queries-editor.service';
import { ResultsService } from '../../../services/result.service';
import { Property } from '../../../viewModels/abc-queries-editor.viewModel';

@Component({
  selector: 'abc-opath-queries-editor-results-json',
  templateUrl: './results-json.component.html',
  styleUrls: ['./results-json.component.scss'],
})
export class ResultsJsonComponent implements OnInit {
  @Input() resultsEmpty: boolean;
  @ViewChild('code') code: ElementRef;
  public data: object = null;

  public executed: boolean = false;
  public loading: boolean = false;
  public translation: {
    executionFailed: string;
    loading: string;
    resultMessage: {
      clickMessage: string;
      button: string;
      viewMessage: string;
    };
  };

  private messageBoxVisible: boolean = false;
  private queries: _AbcTreeNode<Property>;
  private resAsync: Promise<ResourceSet>;
  private $unsubscribe: Subject<void> = new Subject();
  private highlightSyntax: HighlightSyntax = new HighlightSyntax(this.renderer);

  constructor(
    private resultService: ResultsService,
    private abcQueriesEditorService: AbcQueriesEditorService,
    private resourcesService: ResourceManagerService,
    private messageBoxService: MessageBoxService,
    private renderer: Renderer2
  ) {
    this.resAsync = this.resourcesService.getResourceSet('abc-opath-queries-editor');
  }

  async ngOnInit(): Promise<void> {
    this.translation = {
      executionFailed: (await this.resAsync).get('result-ExecutionError'),
      loading: (await this.resAsync).get('result-loading'),
      resultMessage: {
        button: (await this.resAsync).get('menu-executeAsJSON'),
        clickMessage: (await this.resAsync).get('result-ExecutePart1'),
        viewMessage: (await this.resAsync).get('result-ExecutePart2'),
      },
    };

    this.resultService.resultJsonCache && (this.executed = true);
  }

  async ngAfterViewInit(): Promise<void> {
    setTimeout(() => {
      this.code && (this.code.nativeElement.innerHTML = this.resultService.resultJsonCache);
    }, 0);
  }

  public async refreshQueriesData(): Promise<void> {
    this.loading = true;
    this.queries = this.abcQueriesEditorService.queries;
    if (this.queries.children.length) {
      await this.resultService.createObjectQueries(this.queries, this.abcQueriesEditorService.queryDocument);
      this.resultService.queryDocument
        .pipe(
          take(1),
          switchMap((data: QueryObjectsModel) => {
            return this.resultService.executeQueries({ ...data, OutputMode: QueryOutputMode.Json });
          })
        )
        .subscribe(
          (data: Object) => {
            this.data = data;
            this.executed = true;
            this.loading = false;
            if (data) {
              this.resultsEmpty = false;
            }
            setTimeout(() => {
              this.code.nativeElement.innerHTML = this.highlightSyntax.highlight(
                'json',
                JSON.stringify(data, null, 2)
              ).value;
              this.resultService.resultJsonCache = this.code.nativeElement.innerHTML;
            }, 0);
          },
          async (response: HttpErrorResponse) => {
            !this.messageBoxVisible && (await this.showErrorMessage(response));
            this.resultService.resultJsonCache = '';
            this.executed = false;
          }
        );
    } else {
      this.loading = false;
    }
  }

  private async showErrorMessage(response: HttpErrorResponse): Promise<void> {
    this.messageBoxVisible = true;
    this.executed = false;
    this.loading = false;
    const settings = new MessageSettings();
    settings.mode = MessageMode.Error;
    settings.icon = MessageBoxIcon.Error;
    settings.button = MessageBoxButtons.OK;
    settings.message = response.error.Message;
    if (response.error.InnerException) {
      settings.errorMessage =
        response.error.InnerException.Message +
        `\n\n` +
        response.error.InnerException.ExceptionType +
        `\n\n` +
        response.error.InnerException.ExceptionMessage;
    }

    this.messageBoxService
      .showMessageBox(settings)
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((result: DialogResult) => {
        if (DialogResult.OK === result['result']) {
          this.messageBoxVisible = false;
          return;
        }
      });
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }
}
